import React, {
	useEffect,
	useCallback,
	useRef,
	useState,
} from 'react';
import { useFormState } from 'react-hook-form';
import { runMingo, unFlatten } from '@buddy-technology/ion-helpers';
import formatAddressAutocomplete from '../../utils/formatAddressAutocomplete';
import formatAndReplaceAddressAutocompleteValues from '../../utils/formatAndReplaceAddressAutocompleteValues';
import validateAgainstAnotherField from '../../utils/validateAgainstAnotherField';
import { useEventContext, useDataContext } from '../../context';
import { ensureMapsLoaded } from '../../utils/googleMapsLoader';

// Convert new Place.addressComponents → legacy geocoder-like shape existing formatter expects
function toLegacyGeocodeResults(place) {
	const address_components = (place.addressComponents || []).map((c) => ({
		long_name: c.longText || '',
		short_name: c.shortText || '',
		types: c.types || [],
	}));
	return [
		{
			formatted_address: place.formattedAddress || '',
			address_components,
		},
	];
}

export default function AddressAutocomplete(props) {
	const {
		'aria-describedby': ariaDescribedBy,
		id,
		htmlId,
		label = null,
		register,
		errors,
		setError,
		clearErrors,
		isRequired,
		placeholder = 'Type address',
		isDisabled,
		getValues,
		control,
		Controller,
		updateAppState,
		flatApplication,
		setValue,
		trigger,
		fieldsToWatch = [],
		placesApiKey,
	} = props;

	const { eventCallback } = useEventContext();
	const { ion: { variables }, offeringData } = useDataContext();
	const apiKey = placesApiKey;
	// watch + cross-field validation
	const valuesToWatch = fieldsToWatch.map((lbl) => lbl.replace(/\$/g, '').replace(/\./g, '::'));
	const formStateOfValuesToWatch = useFormState({ control, name: valuesToWatch });
	const errorMessages = validateAgainstAnotherField(valuesToWatch, formStateOfValuesToWatch);
	const errorsInMappedFields = errorMessages.length > 0 ? errorMessages.join(', ') : null;
	const hasErrors = !!errors || !!errorsInMappedFields;
	const errorMessage = errors?.message || errorsInMappedFields || null;

	useEffect(() => {
		if (errorsInMappedFields?.length > 0) {
			setError(id, { type: 'custom', message: errorMessage }, { shouldFocus: true });
			eventCallback('onValidationError', { elementId: label, timestamp: Date.now(), validationError: errorMessage });
		}
	}, [errorsInMappedFields]);

	useEffect(() => {
		if (errors) {
			eventCallback('onValidationError', { elementId: label, timestamp: Date.now(), validationError: errors?.type });
		}
	}, [errors?.message]);

	const [input, setInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [suggestions, setSuggestions] = useState([]);
	const [libReady, setLibReady] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [libError, setLibError] = useState(false);
	const debounceTimer = useRef();
	const sessionRef = useRef(null);
	const wrapperRef = useRef(null); // wraps input + list
	const reqIdRef = useRef(0); // prevent late responses from reopening
	const lastCommittedRef = useRef(''); // value from last selection

	// load Maps + Places
	useEffect(() => {
		let mounted = true;
		if (!apiKey) {
			setLibError(true);
			// eslint-disable-next-line no-console
			console.error('No Google Places API key provided');
		} else {
			ensureMapsLoaded(apiKey)
				.then(() => {
					if (mounted) {
						setLibReady(true);
					}
				})
				.catch((e) => {
					if (mounted) {
						setLibError(true);
						// eslint-disable-next-line no-console
						console.error('Failed to load Places', e);
					}
				});
		}
		return () => { mounted = false; };
	}, [apiKey]);

	// close if user clicks/taps outside of suggestions list
	useEffect(() => {
		function handleOutside(e) {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
				setIsOpen(false);
				setActiveIndex(-1);
			}
		}
		document.addEventListener('mousedown', handleOutside);
		document.addEventListener('touchstart', handleOutside, { passive: true });
		return () => {
			document.removeEventListener('mousedown', handleOutside);
			document.removeEventListener('touchstart', handleOutside);
		};
	}, []);

	function nextReqId() {
		reqIdRef.current = (reqIdRef.current ?? 0) + 1;
		return reqIdRef.current;
	}

	// debounced suggestions
	const fetchAddressSuggestions = useCallback(async (q) => {
		if (!libReady || !q || !isOpen) { setSuggestions([]); return; }

		setLoading(true);
		const myId = nextReqId();
		try {
			const { AutocompleteSuggestion, AutocompleteSessionToken } =
				await window.google.maps.importLibrary('places');

			if (!sessionRef.current) {
				sessionRef.current = new AutocompleteSessionToken();
			}

			const { suggestions: s } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
				input: q,
				sessionToken: sessionRef.current,
			});

			if (myId === reqIdRef.current && isOpen) {
				setSuggestions(s || []);
			}
		} catch (_e) {
			if (myId === reqIdRef.current) {
				setSuggestions([]);
			}
		} finally {
			if (myId === reqIdRef.current) {
				setLoading(false);
			}
		}
	}, [libReady, isOpen]);

	useEffect(() => {
		clearTimeout(debounceTimer.current);
		if (input && input.length > 2) {
			debounceTimer.current = setTimeout(() => { fetchAddressSuggestions(input); }, 250);
		} else {
			setSuggestions([]);
		}
		return () => clearTimeout(debounceTimer.current);
	}, [input, fetchAddressSuggestions]);

	// select handler using new Place details
	const handleSelect = async (placePrediction, onChange) => {
		try {
			clearTimeout(debounceTimer.current); // cancel pending fetch
			setIsOpen(false); // hide list immediately
			setActiveIndex(-1);
			setSuggestions([]); // clear current items
			// Convert prediction → Place, then fetch needed fields
			const place = placePrediction.toPlace();
			await place.fetchFields({ fields: ['formattedAddress', 'addressComponents'] });

			const legacyResults = toLegacyGeocodeResults(place);
			const formattedFullAddress = legacyResults[0].formatted_address || '';
			const formattedAddressParts = formatAddressAutocomplete(legacyResults);

			const formValues = getValues();
			const currentField = flatApplication.find((item) => item.id === id);

			if (currentField?.uiDisplay?.translateTo) {
				const mingoResults = runMingo(currentField.uiDisplay.translateTo._calculate, {
					...unFlatten(formValues), variables, addressCompleteResults: formattedAddressParts, offeringData,
				});
				const { value: mingoValue, error: mingoError } = mingoResults;

				if (!mingoError) {
					formatAndReplaceAddressAutocompleteValues(mingoValue, setValue);
					onChange(formattedFullAddress);
				} else {
					// eslint-disable-next-line no-console
					console.log(`There was a mingo ${mingoError}`);
				}
			} else {
				const p = formattedAddressParts;
				const fieldsObjectToCreate = {
					'customer.address.line1': `${p.street_number?.short_name ?? ''} ${p.route?.short_name ?? ''}`.trim(),
					'customer.address.line2': p.subpremise?.short_name ?? '',
					'customer.address.city': p.locality?.short_name ?? '',
					'customer.address.state': p.administrative_area_level_1?.short_name ?? '',
					'customer.address.postalCode': p.postal_code?.short_name ?? '',
					'customer.address.country': p.country?.short_name ?? '',
				};
				formatAndReplaceAddressAutocompleteValues(fieldsObjectToCreate, setValue);
				onChange(formattedFullAddress);
				lastCommittedRef.current = formattedFullAddress; // ← remember last committed value so we don't re-open suggestions list if user tabs into input or clicks in without making changes
			}

			clearErrors(id);
			updateAppState();
			trigger(valuesToWatch);

			// reset session after a selection
			sessionRef.current = null;
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error('Failed to fetch place details:', e);
		}
	};

	return (
		libError ? (
			<div ref={wrapperRef}>
				<p className="input-error" data-testid="error-message">
					Our address prediction service is unavailable. Please check the box below to manually enter your address.
				</p>
			</div>
		) : (
			<Controller
				name={id}
				control={control}
				render={({ field }) => {
					const value = field.value ?? input;
					const listboxId = `${htmlId || id}-listbox`;
					const activeId =
						activeIndex >= 0 && suggestions[activeIndex]
							? `opt-${suggestions[activeIndex].placePrediction.placeId}`
							: undefined;

					return (
						<div ref={wrapperRef}>
							<input
								id={htmlId}
								className={`input-text${errors ? ' input-invalid' : ''} disabled:cursor-not-allowed`}
								data-testid="address-autocomplete-input"
								type="text"
								aria-required={isRequired}
								{...register(id)}
								value={value}
								placeholder={placeholder}
								disabled={isDisabled}
								// ARIA combobox bits
								role="combobox"
								aria-autocomplete="list"
								aria-expanded={isOpen}
								aria-controls={listboxId}
								aria-activedescendant={activeId}
								aria-describedby={ariaDescribedBy}
								onFocus={() => {
									// Do NOT auto-open on focus; only open on typing or arrow keys
									eventCallback('onFocus', { timestamp: Date.now(), elementId: label });
								}}
								onChange={(e) => {
									field.onChange(e.target.value);
									setInput(e.target.value);
									lastCommittedRef.current = ''; // user started editing → allow opening again
									setIsOpen(true); // open because user typed
								}}
								onKeyDown={(e) => {
									// Open on intentional nav keys
									if (!isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
										setIsOpen(true);
									}

									if (e.key === 'ArrowDown') {
										e.preventDefault();
										setActiveIndex((i) => Math.min((i ?? -1) + 1, (suggestions.length || 1) - 1));
									} else if (e.key === 'ArrowUp') {
										e.preventDefault();
										setActiveIndex((i) => Math.max((i ?? 0) - 1, 0));
									} else if (e.key === 'Enter') {
										if (activeIndex >= 0 && suggestions[activeIndex]) {
											e.preventDefault();
											const pred = suggestions[activeIndex].placePrediction;
											handleSelect(pred, field.onChange); // handleSelect closes + remembers value
										}
									} else if (e.key === 'Escape') {
										setIsOpen(false);
										setActiveIndex(-1);
									}
								}}
								onBlur={() => {
									field.onBlur();
									eventCallback('onBlur', { timestamp: Date.now(), elementId: label });
								}}
							/>

							{isOpen && (loading || suggestions.length > 0) && (
								<ul
									role="listbox"
									id={listboxId}
									data-testid="suggestions"
									aria-busy={loading || undefined}
									onMouseLeave={() => setActiveIndex(-1)}
								>
									{loading ? <li>...loading</li> : null}

									{suggestions.map((s, i) => {
										const pred = s.placePrediction;
										const isActive = i === activeIndex;
										const desc =
											pred?.text?.text ||
											(pred?.structuredFormat
												? [pred.structuredFormat.mainText?.text, pred.structuredFormat.secondaryText?.text]
													.filter(Boolean)
													.join(' — ')
												: '');

										return (
											<li
												key={pred.placeId}
												id={`opt-${pred.placeId}`}
												role="option"
												aria-selected={isActive}
												className={`suggestion ${isActive ? 'is-active' : ''}`}
												onMouseEnter={() => setActiveIndex(i)}
												onMouseDown={(e) => {
													// Select on mouse without stealing focus, and don't reopen
													e.preventDefault();
													handleSelect(pred, field.onChange); // closes + sets lastCommittedRef
													setIsOpen(false);
													setActiveIndex(-1);
												}}
												style={{ background: isActive ? '#ececec' : '#fff', cursor: 'pointer', padding: 8 }}
											>
												{desc}
											</li>
										);
									})}
								</ul>
							)}

							{hasErrors ? (
								<p className="input-error" data-testid="error-message">
									{errorMessage}
								</p>
							) : null}
						</div>
					);
				}}
			/>
		)
	);
}
